/* eslint-disable */
import React, { useState, useMemo } from 'react';
import {
  MessageSquare,
  Plus,
  Clock,
  CheckCircle,
  AlertTriangle,
  Send,
  X,
  Upload,
  Paperclip,
  Inbox,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Page from '../../components/Page';
import PageHeader from '../../components/PageHeader';
import useSupportTickets from '../../hooks/useSupportTickets';
const STATUS_STYLES = {
  Open: 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-amber-100 text-amber-700',
  Resolved: 'bg-green-100 text-green-700',
};
const STATUS_BAR = {
  Open: 'bg-blue-500',
  'In Progress': 'bg-amber-500',
  Resolved: 'bg-green-500',
};
const PRIORITY_STYLES = {
  Critical: 'bg-red-100 text-red-700',
  High: 'bg-orange-100 text-orange-700',
  Medium: 'bg-yellow-100 text-yellow-700',
  Low: 'bg-gray-100 text-gray-600',
};
const CATEGORIES = [
  'Assessment Bug',
  'Content Playback',
  'Login/SSO',
  'Certificate Download',
  'Payment Issue',
  'AI Assistant',
  'Other',
];

const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

function UserSupport() {
  const navigate = useNavigate();
  const { tickets, loading, error, fetchTickets, addComment, createTicket } = useSupportTickets();
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  // New ticket drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [categoryError, setCategoryError] = useState(false);
  const [priorityError, setPriorityError] = useState(false);
  const [titleError, setTitleError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const stats = useMemo(() => ({
    open: tickets?.filter((t) => t.status === 'Open').length || 0,
    inProgress: tickets?.filter((t) => t.status === 'In Progress').length || 0,
    resolved: tickets?.filter((t) => t.status === 'Resolved').length || 0,
  }), [tickets]);
  const resetForm = () => {
    setTitle('');
    setCategory('');
    setPriority('Medium');
    setDescription('');
    setFiles([]);
    setSubmitError(null);
    setCategoryError(false);
    setPriorityError(false);
    setTitleError(false);
    setDescriptionError(false);
  };
  const openDrawer = () => {
    resetForm();
    setSubmitSuccess(false);
    setDrawerOpen(true);
  };
  const closeDrawer = () => {
    if (submitting) return;
    setDrawerOpen(false);
    resetForm();
  };
  const handleTicketClick = (ticketId) => {
    setExpandedTicketId(expandedTicketId === ticketId ? null : ticketId);
    setReplyText('');
  };
  const handleReply = async (ticketId) => {
    if (!replyText.trim()) return;
    setReplyLoading(true);
    try {
      await addComment(ticketId, replyText);
      setReplyText('');
    } catch (err) {
      // silently fail or show inline error
    } finally {
      setReplyLoading(false);
    }
  };
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    const missingTitle = !trimmedTitle;
    const missingDescription = !trimmedDescription;
    const missingCategory = !category;
    const missingPriority = !priority;
    setTitleError(missingTitle);
    setDescriptionError(missingDescription);
    setCategoryError(missingCategory);
    setPriorityError(missingPriority);
    if (missingTitle || missingDescription || missingCategory || missingPriority) {
      setSubmitError('Please fill in all required fields before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      await createTicket({ title: trimmedTitle, category, priority, description: trimmedDescription, files });
      setSubmitSuccess(true);
      // Close the drawer shortly after showing the success state
      setTimeout(() => {
        setDrawerOpen(false);
        setSubmitSuccess(false);
        resetForm();
      }, 1500);
    } catch (err) {
      setSubmitError('Failed to submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  const renderStatusBadge = (status) => {
    const style = STATUS_STYLES[status] || 'bg-gray-100 text-gray-600';
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${style}`}>
        {status === 'Open' && <Clock className="w-3 h-3 mr-1" />}
        {status === 'In Progress' && <AlertTriangle className="w-3 h-3 mr-1" />}
        {status === 'Resolved' && <CheckCircle className="w-3 h-3 mr-1" />}
        {status}
      </span>
    );
  };
  const renderPriorityBadge = (pri) => {
    const style = PRIORITY_STYLES[pri] || 'bg-gray-100 text-gray-600';
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${style}`}>
        {pri}
      </span>
    );
  };
  const renderTicketsList = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600 text-sm">Loading your tickets...</span>
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-24 bg-white border border-gray-200 rounded-2xl">
          <AlertTriangle className="w-10 h-10 text-red-400 mb-3" />
          <p className="text-gray-600 text-sm mb-4">{error}</p>
          <button
            onClick={fetchTickets}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      );
    }
    if (!tickets?.length) {
      return (
        <div className="flex flex-col items-center justify-center py-24 bg-white border border-dashed border-gray-300 rounded-2xl">
          <div className="h-14 w-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
            <Inbox className="w-7 h-7" />
          </div>
          <h3 className="text-base font-bold text-gray-900 mb-1">No tickets yet</h3>
          <p className="text-gray-500 text-sm mb-5">Raise a ticket and our support team will get back to you.</p>
          <button
            onClick={openDrawer}
            className="px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Ticket
          </button>
        </div>
      );
    }
    return (
      <div className="space-y-3">
        {tickets?.map((ticket) => (
          <div
            key={ticket.ticket_id}
            className="relative bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
          >
            <span className={`absolute left-0 top-0 bottom-0 w-1 ${STATUS_BAR[ticket.status] || 'bg-gray-300'}`} />
            <button
              type="button"
              onClick={() => handleTicketClick(ticket.ticket_id)}
              className="w-full text-left pl-6 pr-4 py-4 hover:bg-gray-50/70 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <span className="text-xs text-gray-400 font-mono">#{ticket.ticket_id}</span>
                    {renderStatusBadge(ticket.status)}
                    {renderPriorityBadge(ticket.priority)}
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 truncate">{ticket.title}</h4>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs text-gray-500">{ticket.category}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{formatDate(ticket.created_at)}</span>
                  </div>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-gray-400 flex-shrink-0 mt-1 transition-transform ${
                    expandedTicketId === ticket.ticket_id ? 'rotate-180' : ''
                  }`}
                />
              </div>
            </button>
            {expandedTicketId === ticket.ticket_id && (
              <div className="border-t border-gray-100 bg-gray-50/70 p-4 pl-6">
                <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                  {ticket.comments && ticket.comments.length > 0 ? (
                    ticket.comments.map((comment, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-xl text-sm ${
                          comment.is_staff
                            ? 'bg-blue-50 border border-blue-100'
                            : 'bg-white border border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-gray-900 text-xs">
                            {comment.is_staff ? 'Support Team' : 'You'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatDate(comment.created_at)}
                          </span>
                        </div>
                        <p className="text-gray-600">{comment.message}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-2">No comments yet.</p>
                  )}
                </div>
                {ticket.status !== 'Resolved' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleReply(ticket.ticket_id);
                      }}
                      placeholder="Type your reply..."
                      className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => handleReply(ticket.ticket_id)}
                      disabled={replyLoading || !replyText.trim()}
                      className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };
  return (
    <Page title="Help & Support">
      <PageHeader
        pageTitle="Help & Support"
        subTitle="Have an issue? Submit a ticket and our support team will help you out."
        onBack={() => navigate(-1)}
      />
      <div className="px-3 sm:px-6 lg:px-10">
        {/* Stats + New Ticket action */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="grid grid-cols-3 gap-3 flex-1 max-w-xl">
            <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
              <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wide">Open</span>
              <span className="block text-xl font-bold text-blue-600 mt-0.5">{stats.open}</span>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
              <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wide">In Progress</span>
              <span className="block text-xl font-bold text-amber-500 mt-0.5">{stats.inProgress}</span>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
              <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wide">Resolved</span>
              <span className="block text-xl font-bold text-green-600 mt-0.5">{stats.resolved}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={openDrawer}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 shadow-sm transition-colors shrink-0"
          >
            <Plus className="w-4 h-4" />
            New Ticket
          </button>
        </div>
        {/* My Tickets */}
        <div>
          <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-gray-400" />
            My Tickets
          </h2>
          {renderTicketsList()}
        </div>
      </div>
      {/* New Ticket side drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[1300] flex justify-end">
          <button
            type="button"
            aria-label="Close"
            onClick={closeDrawer}
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
          />
          <div className="relative w-full max-w-md h-full bg-white shadow-2xl flex flex-col transition-transform duration-200 ease-out translate-x-0">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
              <div>
                <h3 className="text-base font-bold text-gray-900">New Support Ticket</h3>
                <p className="text-xs text-gray-500 mt-0.5">We usually respond within 24 hours.</p>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="h-9 w-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <X className="w-4.5 h-4.5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5">
              {submitSuccess ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="h-14 w-14 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-7 h-7" />
                  </div>
                  <h4 className="text-base font-bold text-gray-900 mb-1">Ticket Submitted!</h4>
                  <p className="text-gray-500 text-sm">Our support team will get back to you soon.</p>
                </div>
              ) : (
                <form id="new-ticket-form" onSubmit={handleSubmitTicket} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => {
                        setTitle(e.target.value);
                        if (titleError) setTitleError(false);
                      }}
                      placeholder="Brief summary of your issue"
                      className={`w-full px-3 py-2.5 border rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        titleError ? 'border-red-300 bg-red-50/50' : 'border-gray-200'
                      }`}
                    />
                    {titleError && (
                      <p className="text-xs text-red-600 font-medium mt-1.5">Please enter a title.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <div className={`flex flex-wrap gap-2 p-2 rounded-xl border ${categoryError ? 'border-red-300 bg-red-50/50' : 'border-transparent'}`}>
                      {CATEGORIES.map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => {
                            setCategory(cat);
                            setCategoryError(false);
                          }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                            category === cat
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                    {categoryError && (
                      <p className="text-xs text-red-600 font-medium mt-1.5">Please select a category.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                      Priority <span className="text-red-500">*</span>
                    </label>
                    <div className={`flex flex-wrap gap-2 p-2 rounded-xl border ${priorityError ? 'border-red-300 bg-red-50/50' : 'border-transparent'}`}>
                      {PRIORITIES.map((pri) => (
                        <button
                          key={pri}
                          type="button"
                          onClick={() => {
                            setPriority(pri);
                            setPriorityError(false);
                          }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                            priority === pri
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {pri}
                        </button>
                      ))}
                    </div>
                    {priorityError && (
                      <p className="text-xs text-red-600 font-medium mt-1.5">Please select a priority.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => {
                        setDescription(e.target.value);
                        if (descriptionError) setDescriptionError(false);
                      }}
                      rows={5}
                      placeholder="Describe your issue in detail. Include steps to reproduce if applicable."
                      className={`w-full px-3 py-2.5 border rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                        descriptionError ? 'border-red-300 bg-red-50/50' : 'border-gray-200'
                      }`}
                    />
                    {descriptionError && (
                      <p className="text-xs text-red-600 font-medium mt-1.5">Please enter a description.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1.5">Attachments</label>
                    <label className="flex items-center justify-center gap-2 px-4 py-3 border border-dashed border-gray-300 rounded-xl text-sm text-gray-500 cursor-pointer hover:border-blue-400 hover:text-blue-600 transition-colors">
                      <Upload className="w-4 h-4" />
                      Choose Files
                      <input type="file" multiple onChange={handleFileChange} className="hidden" />
                    </label>
                    {files.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {files.map((file, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <Paperclip className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <span className="text-xs text-gray-600 truncate">{file.name}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(idx)}
                              className="p-0.5 text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {submitError && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <p className="text-sm text-red-600">{submitError}</p>
                    </div>
                  )}
                </form>
              )}
            </div>
            {!submitSuccess && (
              <div className="px-5 py-4 border-t border-gray-200 shrink-0">
                <button
                  type="submit"
                  form="new-ticket-form"
                  disabled={submitting}
                  className="w-full py-3 bg-blue-600 text-white font-semibold text-sm rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit Ticket
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </Page>
  );
}

export default UserSupport;
